<script setup lang="ts">
import { useCollectionFilter } from "@/composables/collections/useCollectionFilter";
import { useCollectionsQueries } from "@/composables/useCollectionsQueries";
import { Collection, AppEnum } from "@/services/fobizzToolsApi";
import {
  CollectionFilterParams,
  FilterChangeData,
} from "@/types/collection.types";
import { FilterableRoute } from "@/types/filter.types";

import { Link } from "../BreadCrumb.vue";

type Props = {
  collection: Collection;
};

const props = defineProps<Props>();

const { t } = useI18n();
const userStore = useUserStore();
const international = computed(() => userStore.user?.international);

const route = useRoute();

const { useListRecommendedCollectionsQuery } = useCollectionsQueries();

const collectionItems = computed(
  () =>
    props.collection.collectionItems?.length && props.collection.collectionItems
);

const defaultParams: CollectionFilterParams = {
  query: "",
  schoolTypes: [],
  gradeLevels: [],
  subjectTypes: [],
  models: [],
  page: 1,
  limit: 10,
};

const {
  params,
  updateFilter,
  resetFilters,
  hasActiveFilters,
  debouncedSearch,
  search,
  filteredItems,
} = useCollectionFilter(defaultParams, collectionItems);

const { categories } = useGalleryCategories(
  computed(() => international.value?.toString()),
  computed(() => route.name as FilterableRoute)
);

const handleSearchInput = (value: string) => {
  debouncedSearch(value);
};

const handleSubmit = (query: string) => {
  search(query);
};

const handleFilterChange = (data: FilterChangeData) => {
  updateFilter(data.label, data.value);
};

const handleShare = () => console.error("handleShare Not implemented");
const handleLike = () => console.error("handleLike Not implemented");

const handleResetFilters = () => {
  resetFilters();
};

const { data: collections } = useListRecommendedCollectionsQuery({
  id: props.collection.id,
});

const recommendedCollections = computed(
  () => collections.value?.length && collections.value
);

const links: Link[] = [
  {
    title: t("collections.links.breadCrumbIndex"),
    to: {
      name: "gallery-index",
    },
  },
];
</script>

<template>
  <BreadCrumb :links="links" :title="collection.name" />
  <article class="collection__wrapper">
    <CollectionsHeader
      :collection="collection"
      @share="handleShare"
      @like="handleLike"
    />
    <BaseAlert>
      <span v-safe-html="t('collections.labels.licenseInfo')"></span>
    </BaseAlert>
    <FilterContent
      :categories="categories"
      :query="params.query"
      :query-placeholder="t('collections.searchPlaceholder')"
      :has-query="hasActiveFilters"
      :current-params="params"
      @query="handleSearchInput"
      @submit="handleSubmit"
      @update:model-value="handleFilterChange"
      @reset="handleResetFilters"
    />

    <GalleryMaterialGrid v-if="filteredItems.length">
      <template
        v-for="(collectionItem, itemIndex) in filteredItems"
        :key="itemIndex"
      >
        <MaterialTileAssistant
          v-if="collectionItem.collectionable.kind === AppEnum.AI_ASSISTANT"
          :key="itemIndex"
          :item="collectionItem.collectionable"
        />
        <MaterialTileItem v-else :item="collectionItem.collectionable" />
      </template>
    </GalleryMaterialGrid>
    <BaseEmptyStateAlert v-else />
  </article>
  <template v-if="recommendedCollections">
    <h2 class="collection__recommendations-heading">
      {{ t("collections.headings.collections") }}
    </h2>
    <div class="collection__recommendations">
      <LinkTile
        :title="t('collections.howTo.title')"
        link="/collections/how_to"
        :link-name="t('collections.links.learnMore')"
        icon="info-circle"
      />
      <CollectionTile
        v-for="(recommended, recommendedIndex) in recommendedCollections"
        :key="recommendedIndex"
        :collection="recommended"
      />
    </div>
  </template>
</template>
<style scoped lang="scss">
.collection {
  &__wrapper {
    background-color: $color-white;
    border-radius: $border-radius-sm;
    border: 1px solid $border-color;
    box-shadow: $shadow-brand;
    padding: $spacer-4;
    display: grid;
    gap: $spacer-2;
  }

  &__recommendations {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: $spacer-3;

    @include media-breakpoint-down(xl) {
      grid-template-columns: 1fr 1fr 1fr;
    }

    @include media-breakpoint-down(lg) {
      grid-template-columns: 1fr 1fr;
    }

    @include media-breakpoint-down(md) {
      grid-template-columns: 1fr;
    }
  }

  &__recommendations-heading {
    margin-top: $spacer-6;
    margin-bottom: $spacer-3;

    @include media-breakpoint-down(lg) {
      margin-top: $spacer-5;
    }
  }
}
</style>
